interface ApiErrorShape {
  data?: { errors?: Record<string, string[]>; message?: string }
  message?: string
  status?: number
}

const isApiError = (err: unknown): err is ApiErrorShape => typeof err === 'object' && err !== null

/**
 * The server read the request and refused it (4xx), so it changed nothing.
 *
 * ⚠ Anything else leaves the outcome unknown. With no response, the request may still have been carried
 * out, and a gateway's 502 or 504 can arrive after the work was committed.
 */
const isRequestRefused = (error: unknown): boolean =>
  isApiError(error) && error.status !== undefined && error.status >= 400 && error.status <= 499

const extractApiErrors = (error: unknown, fallbackMessage: string): string[] => {
  if (!isApiError(error)) return [fallbackMessage]

  const errorData = error.data ?? {}

  if (errorData.errors) {
    return Object.values(errorData.errors).flat()
  }

  if (errorData.message ?? error.message) {
    return [errorData.message ?? error.message ?? 'Unknown error']
  }

  return [fallbackMessage]
}

/**
 * Returns the Laravel field-keyed validation errors (e.g. `{ 'media.0': [...] }`)
 * from a thrown API error, or an empty object when none are present.
 */
const getFieldErrors = (error: unknown): Record<string, string[]> => {
  if (!isApiError(error)) return {}
  return error.data?.errors ?? {}
}

const isAbortError = (err: unknown): boolean => {
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (err instanceof Error) {
    // apiClient attaches a `data` property to thrown Errors via Object.assign
    // (see ApiClient.request's `structuredError`) — not part of the standard Error shape.
    const data = (err as Error & { data?: { name?: string } }).data
    if (data?.name === 'AbortError') return true
  }
  return false
}

export { extractApiErrors, getFieldErrors, isAbortError, isApiError, isRequestRefused }
export type { ApiErrorShape }
